import { PaneChannelTiming, PaneChannelTimings } from './AnimationTimingTypes';
import { Animation } from './BaseAnimation';
import { DataStorePaneState } from './DataStorePaneState';
import { IDataStore } from './IDataStore';
import { NonMarksAnimationController } from './NonMarksAnimationController';
import { PaneStateAttribute } from './PaneStateAttribute';
import { WarpingFunction } from './WarpingFunctionMapUtil';

/**
 * Callback function type for pane state updates.
 * Called whenever the animation interpolates a new frame.
 *
 * @param frame - The interpolated datastore for this frame
 */
export type PaneStateUpdateCallback = (frame: IDataStore) => void;

/**
 * Animation that interpolates between two DataStorePaneState objects.
 *
 * This class extends the base Animation class and implements the update() method
 * to perform interpolation between a start state and an end state based on the
 * current animation fraction.
 *
 * Integrates NonMarksAnimationController for grid/axis cross-fade animation,
 * matching the C++ behavior where grid lines and axis labels fade out/in during transitions.
 *
 * Port of C++ PaneStateAnimation from tabanim/main/panestateanimation/PaneStateAnimation.cpp
 *
 * Example usage:
 * ```typescript
 * const startState = new DataStorePaneState(startDataStore);
 * const endState = new DataStorePaneState(endDataStore);
 *
 * const animation = new PaneStateAnimation(
 *   startState,
 *   endState,
 *   (frame) => {
 *     // This callback is called every frame with the interpolated data
 *     renderFrame(frame);
 *   }
 * );
 *
 * // Play the animation
 * AnimationManager.getInstance().playAnimation(animation, duration, style);
 * ```
 */
export class PaneStateAnimation extends Animation {
  private fromState: DataStorePaneState;
  private toState: DataStorePaneState;
  private onUpdate?: PaneStateUpdateCallback;
  /** Clean TO base without injected exits, used for the completed frame. */
  private originalToBase: IDataStore | null = null;

  /** Grid/axis cross-fade animation (null if nothing to animate) */
  private gridAxisAnimation: Animation | null = null;

  /** Resolved nonMarks timing for grid/axis/pane-bounds interpolation (null = full-range [0,1]) */
  private nonMarksTiming: PaneChannelTiming | null = null;
  private nonMarkAttributeTimingOverrides: Map<PaneStateAttribute, PaneChannelTiming> | null = null;

  /** Per-pane channel timings for per-mark enter/move/exit interpolation */
  private paneChannelTimingsMap: Map<string, PaneChannelTimings> | null = null;
  private animatingPaneIds: Set<string> | null = null;
  private overallWarpingFunction: WarpingFunction | null = null;

  /**
   * Create a new PaneStateAnimation.
   *
   * @param fromState - The starting pane state
   * @param toState - The ending pane state
   * @param onUpdate - Optional callback fired whenever a new frame is interpolated
   */
  constructor(
    fromState: DataStorePaneState,
    toState: DataStorePaneState,
    onUpdate?: PaneStateUpdateCallback,
    originalToBase?: IDataStore,
    nonMarksTiming?: PaneChannelTiming | null,
    nonMarkAttributeTimingOverrides?: Map<PaneStateAttribute, PaneChannelTiming> | null,
    paneChannelTimingsMap?: Map<string, PaneChannelTimings> | null,
    animatingPaneIds?: Set<string> | null,
    overallWarpingFunction?: WarpingFunction | null
  ) {
    super();
    this.fromState = fromState;
    this.toState = toState;
    this.onUpdate = onUpdate;
    this.originalToBase = originalToBase ?? null;
    this.nonMarksTiming = nonMarksTiming ?? null;
    this.nonMarkAttributeTimingOverrides = nonMarkAttributeTimingOverrides ?? null;
    this.paneChannelTimingsMap = paneChannelTimingsMap ?? null;
    this.animatingPaneIds = animatingPaneIds ?? null;
    this.overallWarpingFunction = overallWarpingFunction ?? null;

    // Create grid/axis cross-fade animation (matches C++ AddGridAnim in AttemptAnimation)
    this.initGridAxisAnimation();
  }

  /**
   * Update method called whenever the animation fraction changes.
   *
   * This performs the interpolation between fromState and toState based on
   * the current fraction and fires the onUpdate callback with the result.
   *
   * When fraction >= 1.0 (animation complete), returns the actual TO datastore
   * to ensure all fields (including headers, non-animated data) are correct.
   */
  public update(): void {
    const fraction = this.getFraction();
    const effectiveFraction = this.overallWarpingFunction ? this.overallWarpingFunction(fraction) : fraction;

    // When animation completes, return the actual TO state (not interpolated)
    // This ensures all fields (headers, metadata, etc.) are correct
    if (effectiveFraction >= 1.0) {
      const finalFrame = (this.originalToBase ?? this.toState.getBaseDataStore()).clone();
      if (this.onUpdate) {
        this.onUpdate(finalFrame);
      }
      return;
    }

    // Interpolate from start state toward end state (marks + pane bounds)
    // Use per-channel interpolation when channel timings are available
    if (this.paneChannelTimingsMap !== null) {
      this.fromState.interpolateWithChannelTimings(
        this.toState,
        effectiveFraction,
        this.paneChannelTimingsMap,
        this.nonMarksTiming,
        this.nonMarkAttributeTimingOverrides,
        this.animatingPaneIds
      );
    } else {
      this.fromState.interpolate(
        this.toState,
        effectiveFraction,
        this.nonMarksTiming,
        this.nonMarkAttributeTimingOverrides,
        this.animatingPaneIds
      );
    }

    // Apply grid/axis cross-fade animation on top of the interpolated state
    // This writes grid/axis overrides to fromState before toDataStore() is called
    if (this.gridAxisAnimation) {
      this.gridAxisAnimation.setFraction(effectiveFraction);
    }

    // Convert the interpolated state to a plain datastore.
    // We use TO's base as the foundation so new marks (entering marks) are included from the start.
    // fromState collects all the interpolated values and acts as our output buffer.
    // If the value does not change, it will be taken from TO's base.
    // This ensures drill-down animations work correctly and axis labels update immediately.
    // C++ Reference: outState is initialized from targetDataStore (TO).
    const interpolatedFrame = this.fromState.toDataStore(this.toState.getBaseDataStore());

    // Fire callback if provided
    if (this.onUpdate) {
      this.onUpdate(interpolatedFrame);
    }
  }

  /**
   * Get the start state of this animation.
   */
  public getFromState(): DataStorePaneState {
    return this.fromState;
  }

  /**
   * Get the end state of this animation.
   */
  public getToState(): DataStorePaneState {
    return this.toState;
  }

  /**
   * Get the update callback (if any).
   */
  public getUpdateCallback(): PaneStateUpdateCallback | undefined {
    return this.onUpdate;
  }

  /**
   * Get the grid/axis animation (for testing).
   */
  public getGridAxisAnimation(): Animation | null {
    return this.gridAxisAnimation;
  }

  /**
   * Initialize the grid/axis cross-fade animation.
   * C++ Reference: AddGridAnim() in MarkAnimationController::AttemptAnimation()
   *
   * - fromState (src):    The chart state before animation — provides FROM values for interpolation.
   *                       Also acts as the mutable workspace: mark overrides (from interpolate())
   *                       and grid/axis overrides (from gridAxisAnimation) are both written here.
   * - toState (dest):     The chart state after animation — provides TO values for interpolation.
   *                       Read-only; never mutated during the animation.
   * - fromState (outState): The same object as src, passed as the output target so that grid/axis
   *                       overrides land on fromState.overrides alongside mark overrides.
   *                       This way, one toDataStore() call on fromState collects everything
   *                       (marks + grid + axis) into the final frame sent to the renderer.
   */
  private initGridAxisAnimation(): void {
    const controller = new NonMarksAnimationController();
    this.gridAxisAnimation = controller.createGridAnimation(
      this.fromState,
      this.toState,
      this.fromState, // outState = fromState, so overrides are applied there
      this.nonMarksTiming
    );
  }
}
